import { ReactNode } from 'react';
import { SystemMessageIconKey, SystemMessageType } from '@/models/in/SystemMessageDtoIn';

/**
 * The glyphs a system message can wear beside its title.
 *
 * Drawn inline with currentColor rather than pulled from assets/icons, because the colour is the
 * message's - an icon file with a baked-in fill could not take its tone. All of them follow the
 * house geometry of assets/icons: a 24 grid, 1.5 stroke, round caps and joins.
 */
export const systemMessageIcons: Record<SystemMessageIconKey, ReactNode> = {
  megaphone: (
    <>
      <path
        d="M3 10.5v3a1.5 1.5 0 0 0 1.5 1.5H7l6.5 4V5L7 9H4.5A1.5 1.5 0 0 0 3 10.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M7 15v3.5a1.5 1.5 0 0 0 3 0V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M17 9.5a4 4 0 0 1 0 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M19.5 7a7.5 7.5 0 0 1 0 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
  sparkle: (
    <>
      <path
        d="M12 3.5 13.7 9l5.5 1.7-5.5 1.7L12 18l-1.7-5.6L4.8 10.7 10.3 9 12 3.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M18.5 16v4M16.5 18h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="8" r="1" fill="currentColor" />
      <path d="M12 11.25V16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8.25 12.35 10.9 15l5-5.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  warning: (
    <>
      <path
        d="M20.1 21.1H3.9c-1.55 0-2.52-1.68-1.75-3.02l8.1-14.08a2 2 0 0 1 3.49 0l8.1 14.08c.78 1.34-.2 3.02-1.74 3.02Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path d="M12 9.5v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="17.1" r="1" fill="currentColor" />
    </>
  ),
  alert: (
    <>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 7.5V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="16.25" r="1" fill="currentColor" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.5 10h17" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 3.5V6M16 3.5V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8.5" cy="14" r="1" fill="currentColor" />
      <circle cx="12" cy="14" r="1" fill="currentColor" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 7.5V12l3.2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  tools: (
    <>
      <path
        d="M14.2 6.6a3.4 3.4 0 0 1 4.7 4.4l-8.4 8.4a2 2 0 0 1-2.8-2.8l8.4-8.4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9.4 4.4 4.5 9.3M6.9 3.5 3.6 6.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M4 6.8 8.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
  document: (
    <>
      <path d="M6.5 3.5h7L19 9v11.5H6.5V3.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.5 3.5V9H19" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M9.5 13h6M9.5 16.5h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
  gift: (
    <>
      <rect x="3.5" y="9" width="17" height="11.5" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.5 13.5h17M12 9v11.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12 9C12 9 11.5 4.5 8.75 4.5a2.25 2.25 0 0 0 0 4.5H12Zm0 0c0 0 .5-4.5 3.25-4.5a2.25 2.25 0 0 1 0 4.5H12Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </>
  ),
  heart: (
    <path
      d="M12 20s-7.5-4.4-7.5-9.4A4.1 4.1 0 0 1 12 8.2a4.1 4.1 0 0 1 7.5 2.4c0 5-7.5 9.4-7.5 9.4Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
};

/** What a message wears when the admin picked no icon of their own. */
const defaultIconByType: Record<SystemMessageType, SystemMessageIconKey> = {
  info: 'info',
  success: 'check',
  warning: 'warning',
  error: 'alert',
};

export const iconForMessage = (message: { type: SystemMessageType; iconKey: SystemMessageIconKey | null }): ReactNode => {
  const key = (message.iconKey && systemMessageIcons[message.iconKey] ? message.iconKey : null) ?? defaultIconByType[message.type] ?? 'info';

  return systemMessageIcons[key];
};

export const systemMessageIconKeys = Object.keys(systemMessageIcons) as SystemMessageIconKey[];

/**
 * Which of the two brand colours a type takes.
 *
 * The whole palette is the site's own - wine and blue, no traffic-light green or red - so the type
 * is carried by the glyph beside the title, and the colour splits the four types into the two
 * families that actually behave differently: wine for the ones asking the user to do or notice
 * something, blue for the ones just telling them. That way a warning still reads as more urgent
 * than a notice without borrowing a colour from outside the brand.
 */
export const familyClassForType: Record<SystemMessageType, 'wine' | 'blue'> = {
  info: 'blue',
  success: 'blue',
  warning: 'wine',
  error: 'wine',
};
